'use client';

/**
 * RFI Explorer
 *
 * Project/RFQ tree with message counts. Fetches via rfi_explorer
 * data gateway strategy. Click a project to open a tab; click an
 * RFQ to select it (updates copilot tabs via matrix).
 *
 * @layer presentation
 * @arch_component_type explorer
 * @arch_domain rfi
 */

import { useState, useEffect } from 'react';
import {
  ChevronRight,
  FolderKanban,
  FileText,
  MessageSquare,
  Loader2,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// ── Types (match rfi_explorer response — 3-level tree:
//    project → x_rfq_group_id → rfqs[], per Phase B x_rfq_group_id reshape) ──

interface RFQNode {
  semantic_id: string;
  name: string;
  state: string;
  odoo_id: number;
  vendor_name: string | null;
  vendor_sid: string | null;
  message_count: number;
}

interface RFQGroup {
  x_rfq_group_id: string;          // "" for legacy ungrouped
  created_at: string | null;
  rfq_count: number;
  rfqs: RFQNode[];
}

interface ProjectNode {
  project_sid: string;
  project_name: string;
  rfq_count: number;
  group_count: number;
  rfq_groups: RFQGroup[];
}

interface ExplorerResponse {
  projects: ProjectNode[];
  count?: number;
}

// ── Component ────────────────────────────────────────────────────

export function RFIExplorer() {
  const [projects, setProjects] = useState<ProjectNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    api
      .getStrategyData<ExplorerResponse>('rfi_explorer')
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load RFI data');
          return;
        }
        setProjects(res.data.projects ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Failed to load RFI data. Check connection.
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
        No projects with conversations found.
      </div>
    );
  }

  return (
    <div className="py-1">
      {projects.map((project) => (
        <ProjectTreeItem key={project.project_sid} project={project} />
      ))}
    </div>
  );
}

// ── Tree Item ────────────────────────────────────────────────────

function ProjectTreeItem({ project }: { project: ProjectNode }) {
  const [expanded, setExpanded] = useState(false);
  const openTab = useTabStore((s) => s.openTab);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const hasRFQs = project.rfq_count > 0;

  const handleOpenProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    openTab({
      id: `rfi-project-${project.project_sid}`,
      type: 'project',
      title: project.project_name,
      entityId: project.project_sid,
    });
    selectNode({
      id: project.project_sid,
      label: project.project_name,
      type: 'project',
    });
  };

  return (
    <div>
      {/* Project Row */}
      <div
        className={cn(
          'w-full flex items-center gap-1 px-2 py-1 text-sm',
          'hover:bg-accent/50 transition-colors',
          !hasRFQs && 'pl-6'
        )}
      >
        {hasRFQs && (
          <button onClick={() => setExpanded(!expanded)}>
            <ChevronRight
              className={cn(
                'w-4 h-4 shrink-0 transition-transform',
                expanded && 'rotate-90'
              )}
            />
          </button>
        )}
        <button
          onClick={handleOpenProject}
          className="flex items-center gap-1 flex-1 min-w-0 text-left"
        >
          <FolderKanban className="w-4 h-4 shrink-0 text-primary" />
          <span className="truncate flex-1">{project.project_name}</span>
        </button>
        {hasRFQs && (
          <span className="text-xs text-muted-foreground shrink-0">
            {project.rfq_count} RFQ · {project.group_count} tender
            {project.group_count === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Expanded RFQ Groups */}
      {expanded && (
        <div className="ml-4 border-l border-border/50">
          {(project.rfq_groups ?? []).map((group) => (
            <GroupTreeItem
              key={`${project.project_sid}-${group.x_rfq_group_id || 'legacy'}`}
              project={project}
              group={group}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Group Row + Expanded RFQ Children ────────────────────────────

function GroupTreeItem({
  project,
  group,
}: {
  project: ProjectNode;
  group: RFQGroup;
}) {
  // One-sibling tenders: inline the RFQ, skip the group layer.
  // Legacy null-group RFQs (x_rfq_group_id === ''): render each as its own row.
  const inlineSiblings = !group.x_rfq_group_id || group.rfq_count <= 1;
  const [expanded, setExpanded] = useState(true);

  if (inlineSiblings) {
    return (
      <>
        {group.rfqs
          .filter((r) => r.semantic_id)
          .map((rfq) => (
            <RFQRow key={rfq.semantic_id} rfq={rfq} projectSid={project.project_sid} />
          ))}
      </>
    );
  }

  return (
    <div>
      <div
        className={cn(
          'w-full flex items-center gap-1 px-2 py-1 text-xs',
          'hover:bg-accent/30 transition-colors'
        )}
      >
        <button onClick={() => setExpanded(!expanded)}>
          <ChevronRight
            className={cn(
              'w-3 h-3 shrink-0 transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </button>
        <Layers className="w-3 h-3 shrink-0 text-muted-foreground" />
        <span className="truncate flex-1 text-muted-foreground">
          {group.x_rfq_group_id}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          {group.rfq_count} vendors
        </span>
      </div>
      {expanded && (
        <div className="ml-3 border-l border-border/30">
          {group.rfqs
            .filter((r) => r.semantic_id)
            .map((rfq) => (
              <RFQRow key={rfq.semantic_id} rfq={rfq} projectSid={project.project_sid} />
            ))}
        </div>
      )}
    </div>
  );
}

function RFQRow({ rfq, projectSid }: { rfq: RFQNode; projectSid: string }) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const label = rfq.vendor_name
    ? `${rfq.name} · ${rfq.vendor_name}`
    : rfq.name;

  return (
    <button
      onClick={() =>
        selectNode(
          {
            id: rfq.semantic_id,
            label: rfq.name || 'RFQ',
            type: 'rfq',
            status: rfq.state || undefined,
          },
          projectSid
        )
      }
      className={cn(
        'w-full flex items-center gap-1 px-2 py-1 text-sm text-left',
        'hover:bg-accent/50 pl-4',
        selectedNode?.id === rfq.semantic_id && 'bg-accent'
      )}
    >
      <FileText className="w-4 h-4 shrink-0 text-warning" />
      <span className="truncate flex-1">{label}</span>
      <div className="flex items-center gap-2 shrink-0">
        {rfq.message_count > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <MessageSquare className="w-3 h-3" />
            {rfq.message_count}
          </span>
        )}
        {rfq.state && (
          <span
            className={cn(
              'text-xs px-1.5 py-0.5 rounded',
              rfq.state === 'draft' && 'bg-muted text-muted-foreground',
              rfq.state === 'sent' && 'bg-info/20 text-info',
              rfq.state === 'purchase' && 'bg-warning/20 text-warning',
              rfq.state === 'done' && 'bg-success/20 text-success',
              rfq.state === 'cancel' && 'bg-destructive/20 text-destructive line-through',
            )}
          >
            {rfq.state}
          </span>
        )}
      </div>
    </button>
  );
}
